import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Alert, Badge, Spinner, Modal } from 'react-bootstrap';
import { PersonCheck, Calendar, FileText, X } from 'react-bootstrap-icons';
import { useTracks } from '../hooks/useTracks';
import { formatDateTime } from '../utils/dateUtils';
import config from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

const AssignReviewsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [selectedSubmissions, setSelectedSubmissions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState('');
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [sortField, setSortField] = useState('submittedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const { tracks, loading: tracksLoading } = useTracks();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSubmissions = React.useMemo(() => {
    return [...submissions].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'user':
          aValue = a.userEmail || '';
          bValue = b.userEmail || '';
          break;
        case 'track':
          aValue = a.track || '';
          bValue = b.track || '';
          break;
        case 'title':
          aValue = a.title || '';
          bValue = b.title || '';
          break;
        case 'submittedAt':
          aValue = new Date(a.submittedAt || 0);
          bValue = new Date(b.submittedAt || 0);
          break;
        case 'files':
          aValue = a.files?.length || 0;
          bValue = b.files?.length || 0;
          break;
        case 'reviewers':
          aValue = a.assignedReviewers?.length || 0;
          bValue = b.assignedReviewers?.length || 0;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [submissions, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch submissions and reviewers in parallel
      const [submissionsResponse, reviewersResponse] = await Promise.all([
        fetch(`${config.api.url}admin/submissions?format=json`),
        fetch(`${config.api.url}admin/reviewers?format=json`)
      ]);

      if (!submissionsResponse.ok) {
        throw new Error(`Failed to fetch submissions: ${submissionsResponse.status}`);
      }
      
      if (!reviewersResponse.ok) {
        throw new Error(`Failed to fetch reviewers: ${reviewersResponse.status}`);
      }

      const submissionsData = await submissionsResponse.json();
      const reviewersData = await reviewersResponse.json();

      console.log('Submissions data received:', submissionsData);

      // Group submissions by user and track, keeping only the latest
      const latestSubmissions = groupLatestSubmissions(submissionsData.submissions || []);
      
      console.log('Latest submissions after grouping:', latestSubmissions);
      
      setSubmissions(latestSubmissions);
      setReviewers(reviewersData.reviewers || []);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const groupLatestSubmissions = (allSubmissions) => {
    const grouped = {};
    
    allSubmissions.forEach(submission => {
      const key = `${submission.userId}-${submission.track}`;
      
      if (!grouped[key] || new Date(submission.timestamp) > new Date(grouped[key].timestamp)) {
        grouped[key] = submission;
      }
    });
    
    return Object.values(grouped).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const getTrackName = (trackId) => {
    if (tracksLoading) return trackId;
    const track = tracks.find(t => t.id === trackId);
    return track ? track.name : trackId;
  };

  const handleSubmissionSelect = (submissionId) => {
    const newSelected = new Set(selectedSubmissions);
    if (newSelected.has(submissionId)) {
      newSelected.delete(submissionId);
    } else {
      newSelected.add(submissionId);
    }
    setSelectedSubmissions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSubmissions.size === submissions.length) {
      setSelectedSubmissions(new Set());
    } else {
      setSelectedSubmissions(new Set(submissions.map(s => s.submissionId)));
    }
  };

  const handleSendNotifications = async () => {
    if (selectedSubmissions.size === 0) {
      setError('Please select at least one submission');
      return;
    }

    setSendingNotifications(true);
    setError(null);

    try {
      const response = await fetch(`${config.api.url}admin/notify-reviewers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          submissionIds: Array.from(selectedSubmissions)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Notification failed: ${response.status}`);
      }

      const result = await response.json();
      
      setSuccess(`Successfully sent notifications to ${result.summary.notified} reviewer(s) for ${selectedSubmissions.size} submission(s)`);
      setSelectedSubmissions(new Set());
      setShowNotifyModal(false);
      
    } catch (err) {
      console.error('Error sending notifications:', err);
      setError(`Failed to send notifications: ${err.message}`);
    } finally {
      setSendingNotifications(false);
    }
  };

  const handleAssignReviewer = async () => {
    if (!selectedReviewer || selectedSubmissions.size === 0) {
      setError('Please select a reviewer and at least one submission');
      return;
    }

    setAssigning(true);
    setError(null);

    try {
      const response = await fetch(`${config.api.url}admin/assign-reviewer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          submissionIds: Array.from(selectedSubmissions),
          reviewerId: selectedReviewer
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Assignment failed: ${response.status}`);
      }

      await response.json();
      
      setSuccess(`Successfully assigned ${selectedSubmissions.size} submission(s) to reviewer`);
      setSelectedSubmissions(new Set());
      setSelectedReviewer('');
      setShowAssignModal(false);
      
      // Refresh data to show updated assignments
      await fetchData();
      
    } catch (err) {
      console.error('Error assigning reviewer:', err);
      setError(`Failed to assign reviewer: ${err.message}`);
    } finally {
      setAssigning(false);
    }
  };

  const getStatusBadge = (submission) => {
    const reviewers = submission.assignedReviewers || [];
    
    if (reviewers.length === 0) {
      return <Badge bg="warning">Unassigned</Badge>;
    } else if (reviewers.length === 1) {
      return <Badge bg="info">1 Reviewer</Badge>;
    } else {
      return <Badge bg="success">{reviewers.length} Reviewers</Badge>;
    }
  };

  const handleRemoveReviewer = async (submissionId, reviewId, reviewerEmail) => {
    if (!window.confirm(`Remove ${reviewerEmail} from this submission?`)) {
      return;
    }

    try {
      const response = await fetch(`${config.api.url}admin/remove-reviewer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          submissionId: submissionId,
          reviewId: reviewId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to remove reviewer: ${response.status}`);
      }

      setSuccess(`Successfully removed reviewer ${reviewerEmail}`);
      
      // Refresh data to show updated assignments
      await fetchData();
      
    } catch (err) {
      console.error('Error removing reviewer:', err);
      setError(`Failed to remove reviewer: ${err.message}`);
    }
  };

  const getReviewersDisplay = (submission) => {
    const reviewers = submission.assignedReviewers || [];
    
    if (reviewers.length === 0) {
      return <span className="text-muted">No reviewers assigned</span>;
    }
    
    return (
      <div>
        {reviewers.map((reviewer, index) => (
          <div key={reviewer.id} className="mb-1 d-flex align-items-center">
            <Badge bg="light" text="dark" className="me-1">
              {reviewer.email}
            </Badge>
            {reviewer.status === 'in_progress' && (
              <Badge bg="primary" size="sm" className="me-1">In Progress</Badge>
            )}
            <Button
              variant="outline-danger"
              size="sm"
              className="p-0 border-0"
              style={{ width: '20px', height: '20px' }}
              onClick={() => handleRemoveReviewer(submission.submissionId, reviewer.reviewId, reviewer.email)}
              title={`Remove ${reviewer.email}`}
            >
              <X size={12} />
            </Button>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading submissions and reviewers..." />;
  }

  return (
    <Container className="py-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2><PersonCheck className="me-2" />Assign Reviews</h2>
              <p className="text-muted">Assign reviewers to the latest submissions from each user per track</p>
            </div>
            <div>
              <Button 
                variant="success" 
                className="me-2"
                onClick={() => setShowNotifyModal(true)}
                disabled={selectedSubmissions.size === 0}
              >
                📧 Notify Reviewers ({selectedSubmissions.size})
              </Button>
              <Button 
                variant="primary" 
                onClick={() => setShowAssignModal(true)}
                disabled={selectedSubmissions.size === 0}
              >
                Add Reviewer to Selected ({selectedSubmissions.size})
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Latest Submissions by User & Track</h5>
                <Form.Check
                  type="checkbox"
                  label="Select All"
                  checked={selectedSubmissions.size === submissions.length && submissions.length > 0}
                  onChange={handleSelectAll}
                />
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {submissions.length === 0 ? (
                <div className="text-center py-4">
                  <FileText size={48} className="text-muted mb-3" />
                  <p className="text-muted">No submissions found</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th width="50">
                        <Form.Check
                          type="checkbox"
                          checked={selectedSubmissions.size === submissions.length && submissions.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th style={{cursor: 'pointer'}} onClick={() => handleSort('user')}>
                        Submission {sortField === 'user' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{cursor: 'pointer'}} onClick={() => handleSort('track')}>
                        Track {sortField === 'track' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{cursor: 'pointer'}} onClick={() => handleSort('submittedAt')}>
                        Submitted {sortField === 'submittedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{cursor: 'pointer'}} onClick={() => handleSort('files')}>
                        Files {sortField === 'files' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{cursor: 'pointer'}} onClick={() => handleSort('reviewers')}>
                        Reviewers {sortField === 'reviewers' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{cursor: 'pointer'}} onClick={() => handleSort('status')}>
                        Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSubmissions.map((submission) => (
                      <tr key={submission.submissionId}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedSubmissions.has(submission.submissionId)}
                            onChange={() => handleSubmissionSelect(submission.submissionId)}
                          />
                        </td>
                        <td>
                          <div>
                            <strong>{submission.title}</strong>
                            <br />
                            <small className="text-muted">{submission.userEmail}</small>
                          </div>
                        </td>
                        <td>
                          <Badge bg="secondary">{getTrackName(submission.track)}</Badge>
                        </td>
                        <td>
                          <div>
                            <Calendar className="me-1" size={14} />
                            {formatDateTime(submission.timestamp)}
                          </div>
                        </td>
                        <td>
                          <Badge bg="light" text="dark">
                            {submission.fileCount || 0} files
                          </Badge>
                        </td>
                        <td>
                          {getReviewersDisplay(submission)}
                        </td>
                        <td>
                          {getStatusBadge(submission)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Assign Reviewer Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Reviewer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Assign a reviewer to {selectedSubmissions.size} selected submission(s):</p>
          
          {/* Show existing reviewers for selected submissions */}
          {false && selectedSubmissions.size > 0 && (
            <div className="mb-3">
              <h6>Current Assignments:</h6>
              {Array.from(selectedSubmissions).map(submissionId => {
                const submission = submissions.find(s => s.submissionId === submissionId);
                const reviewers = submission?.assignedReviewers || [];
                
                return (
                  <div key={submissionId} className="mb-2 p-2 bg-light rounded">
                    <div className="fw-bold">{submission?.title}</div>
                    <div className="small text-muted">
                      {reviewers.length === 0 ? (
                        'No reviewers assigned'
                      ) : (
                        `Current reviewers: ${reviewers.map(r => r.email).join(', ')}`
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>Add Reviewer</Form.Label>
            <Form.Select
              value={selectedReviewer}
              onChange={(e) => setSelectedReviewer(e.target.value)}
            >
              <option value="">Choose a reviewer to add...</option>
              {reviewers.map((reviewer) => (
                <option key={reviewer.id} value={reviewer.id}>
                  {reviewer.name} ({reviewer.email})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              This reviewer will be added to all selected submissions. Existing reviewers will remain assigned.
            </Form.Text>
          </Form.Group>

          {reviewers.length === 0 && (
            <Alert variant="warning">
              No reviewers found. Please add reviewers in the User Management section first.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignReviewer}
            disabled={!selectedReviewer || assigning}
          >
            {assigning ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding Reviewer...
              </>
            ) : (
              'Add Reviewer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Notify Reviewers Modal */}
      <Modal show={showNotifyModal} onHide={() => setShowNotifyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>📧 Notify Reviewers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Send email notifications to reviewers assigned to {selectedSubmissions.size} selected submission(s):</p>
          
          {/* Show submissions and their assigned reviewers */}
          {selectedSubmissions.size > 0 && (
            <div className="mb-3">
              <h6>Notifications will be sent for:</h6>
              {Array.from(selectedSubmissions).map(submissionId => {
                const submission = submissions.find(s => s.submissionId === submissionId);
                const reviewers = submission?.assignedReviewers || [];
                
                return (
                  <div key={submissionId} className="mb-2 p-2 bg-light rounded">
                    <div className="fw-bold">{submission?.title}</div>
                    <div className="small text-muted">
                      {reviewers.length === 0 ? (
                        <span className="text-warning">⚠️ No reviewers assigned - no notifications will be sent</span>
                      ) : (
                        <span className="text-success">
                          ✅ Will notify: {reviewers.map(r => r.email).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Alert variant="info">
            <Alert.Heading>📧 Email Content</Alert.Heading>
            <p>Reviewers will receive an email containing:</p>
            <ul>
              <li>Submission title and details</li>
              <li>Author information</li>
              <li>Review deadline (if configured)</li>
              <li>Link to review interface</li>
            </ul>
          </Alert>

          {/* Check if any selected submissions have no reviewers */}
          {Array.from(selectedSubmissions).some(submissionId => {
            const submission = submissions.find(s => s.submissionId === submissionId);
            return !submission?.assignedReviewers || submission.assignedReviewers.length === 0;
          }) && (
            <Alert variant="warning">
              <strong>Note:</strong> Some selected submissions have no assigned reviewers. 
              No notifications will be sent for those submissions.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotifyModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleSendNotifications}
            disabled={sendingNotifications}
          >
            {sendingNotifications ? (
              <>
                <Spinner size="sm" className="me-2" />
                Sending Notifications...
              </>
            ) : (
              '📧 Send Notifications'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AssignReviewsPage;
